import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search, X } from "lucide-react";

/**
 * Controlled searchable select.
 *
 * Props:
 *   value      : currently selected option value (string)
 *   onChange   : (newValue) => void
 *   options    : [{ value, label, sublabel? }, ...]
 *   placeholder: string
 *   disabled   : bool
 *   testid     : string
 *   allowClear : bool
 *   renderSelected: optional (option) => ReactNode for the selected value
 */
export default function SearchableSelect({
  value,
  onChange,
  options = [],
  placeholder = "Select…",
  disabled = false,
  testid = "searchable-select",
  allowClear = false,
  portal = false,
  containerClassName = "",
  triggerClassName = "",
  showSelectedSublabel = true,
  hideIndicator = false,
  onSearchChange = null,
  searchLoading = false,
  renderSelected = null,
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const menuRef = useRef(null);
  const [menuPosition, setMenuPosition] = useState(null);
  const userSearchStartedRef = useRef(false);
  const remoteLoadOnOpenRef = useRef(false);

  useEffect(() => {
    const onDocClick = (e) => {
      if (
        wrapRef.current &&
        !wrapRef.current.contains(e.target) &&
        !menuRef.current?.contains(e.target)
      ) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open || typeof onSearchChange !== "function") return undefined;
    if (!userSearchStartedRef.current && !remoteLoadOnOpenRef.current) return undefined;
    const timeout = setTimeout(() => onSearchChange(query.trim()), 250);
    return () => clearTimeout(timeout);
  }, [onSearchChange, open, query]);

  useEffect(() => {
    if (!open || !portal) return undefined;
    const positionMenu = () => {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      setMenuPosition({ left: rect.left, top: rect.bottom + 4, width: rect.width });
    };
    positionMenu();
    window.addEventListener("resize", positionMenu);
    window.addEventListener("scroll", positionMenu, true);
    return () => {
      window.removeEventListener("resize", positionMenu);
      window.removeEventListener("scroll", positionMenu, true);
    };
  }, [open, portal]);

  const selected = options.find((o) => o.value === value);
  const q = query.trim().toLowerCase();
  const filtered = !q
    ? options
    : options.filter(
        (o) =>
          o.label.toLowerCase().includes(q) ||
          (o.sublabel || "").toLowerCase().includes(q) ||
          (o.value || "").toLowerCase().includes(q)
      );

  const select = (val) => {
    onChange(val);
    setOpen(false);
  };

  const toggleOpen = () => {
    if (!open) {
      userSearchStartedRef.current = false;
      remoteLoadOnOpenRef.current = options.length === 0;
      setQuery("");
    }
    setOpen((current) => !current);
  };

  return (
    <div className={`relative ${containerClassName}`} ref={wrapRef} data-testid={testid}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && toggleOpen()}
        className={`w-full border border-zinc-300 bg-card px-3 py-2.5 text-sm rounded-lg flex items-center justify-between gap-2 text-left transition-colors ${
          open ? "border-primary ring-2 ring-primary/20" : "hover:border-primary/50"
        } ${disabled ? "opacity-50 cursor-not-allowed" : ""} ${triggerClassName}`}
        data-action-category="utility"
        data-testid={`${testid}-trigger`}
      >
        {selected ? (
            <span className="flex-1 min-w-0">
            <span className="block truncate font-medium">
              {typeof renderSelected === "function" ? renderSelected(selected) : selected.label}
            </span>
            {showSelectedSublabel && selected.sublabel && (
              <span className="block truncate text-[11px] font-medium tracking-[0.01em] text-zinc-500">
                {selected.sublabel}
              </span>
            )}
          </span>
        ) : (
          <span className="text-zinc-400 font-normal">{placeholder}</span>
        )}
        {!hideIndicator ? <div className="flex items-center gap-1 shrink-0">
          {allowClear && value && (
            <span
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                onChange("");
              }}
              className="cursor-pointer rounded-md p-1 text-zinc-400 hover:bg-accent hover:text-destructive"
              aria-label="Clear selection"
              data-action-category="utility"
              data-testid={`${testid}-clear`}
            >
              <X className="w-3.5 h-3.5" />
            </span>
          )}
          <ChevronDown
            className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${open ? "rotate-180" : ""}`}
          />
        </div> : null}
      </button>

      {open && (!portal || menuPosition) && (portal ? createPortal(
        <div
          ref={menuRef}
          className={`${portal ? "fixed z-[101]" : "absolute z-50 mt-1 w-full"} bg-popover border border-zinc-200 shadow-brutal max-h-72 overflow-hidden rounded-lg`}
          style={portal ? menuPosition : undefined}
          data-testid={`${testid}-menu`}
        >
          <div className="relative border-b border-zinc-200">
            {!query && <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />}
            <input
              ref={inputRef}
              value={query}
              onChange={(e) => {
                userSearchStartedRef.current = true;
                setQuery(e.target.value);
              }}
              placeholder="Search…"
              className="w-full pl-8 pr-3 py-2.5 text-xs outline-none bg-zinc-50 text-foreground placeholder:text-muted-foreground"
              data-testid={`${testid}-search`}
            />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchLoading ? (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">
                Searching...
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">
                No matches.
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => select(o.value)}
                  className={`w-full text-left px-3 py-2.5 text-xs hover:bg-accent border-b last:border-b-0 border-zinc-100 ${
                    o.value === value ? "bg-primary-soft" : ""
                  }`}
                  data-action-category="utility"
                  data-testid={`${testid}-opt-${o.value}`}
                >
                  <div className="font-medium text-zinc-900">{o.label}</div>
                  {o.sublabel && (
                    <div className={`mt-0.5 text-[10px] font-medium tracking-[0.01em] ${o.sublabelClassName || "text-zinc-500"}`}>
                      {o.sublabel}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>, document.body) : (
        <div
          ref={menuRef}
          className="absolute z-50 mt-1 w-full bg-popover border border-zinc-200 shadow-brutal max-h-72 overflow-hidden rounded-lg"
          data-testid={`${testid}-menu`}
        >
          <div className="relative border-b border-zinc-200">
            {!query && <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-400" />}
            <input ref={inputRef} value={query} onChange={(e) => {
              userSearchStartedRef.current = true;
              setQuery(e.target.value);
            }} placeholder="Search…" className="w-full pl-8 pr-3 py-2.5 text-xs outline-none bg-zinc-50 text-foreground placeholder:text-muted-foreground" data-testid={`${testid}-search`} />
          </div>
          <div className="max-h-56 overflow-y-auto">
            {searchLoading ? (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">
                Searching...
              </div>
            ) : filtered.length === 0 ? (
              <div className="px-3 py-4 text-center text-xs text-zinc-500">
                No matches.
              </div>
            ) : (
              filtered.map((o) => (
                <button
                  type="button"
                  key={o.value}
                  onClick={() => select(o.value)}
                  className={`w-full text-left px-3 py-2.5 text-xs hover:bg-accent border-b last:border-b-0 border-zinc-100 ${o.value === value ? "bg-primary-soft" : ""}`}
                  data-action-category="utility"
                  data-testid={`${testid}-opt-${o.value}`}
                >
                  <div className="font-medium text-zinc-900">{o.label}</div>
                  {o.sublabel && (
                    <div className={`mt-0.5 text-[10px] font-medium tracking-[0.01em] ${o.sublabelClassName || "text-zinc-500"}`}>
                      {o.sublabel}
                    </div>
                  )}
                </button>
              ))
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
